import { CONNECTION_STATE_LABELS, type ConnectionState } from '@convometry/contracts';
import { AlertOctagon, CheckCircle2, Loader2, LogOut, PauseCircle, PlugZap, QrCode, RefreshCw, Smartphone, Unplug } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { Button } from '../primitives/button.js';

const STATE_STYLE: Record<ConnectionState, { cls: string; icon: React.ReactNode }> = {
  connected: { cls: 'border-success/25 bg-success-bg text-success', icon: <CheckCircle2 aria-hidden /> },
  syncing: { cls: 'border-info/25 bg-info-bg text-info', icon: <RefreshCw aria-hidden className="animate-spin [animation-duration:2s]" /> },
  connecting: { cls: 'border-info/25 bg-info-bg text-info', icon: <Loader2 aria-hidden className="animate-spin" /> },
  reconnecting: { cls: 'border-warning/25 bg-warning-bg text-warning', icon: <Loader2 aria-hidden className="animate-spin" /> },
  awaiting_qr: { cls: 'border-info/25 bg-info-bg text-info', icon: <QrCode aria-hidden /> },
  paused: { cls: 'border-border-subtle bg-surface-subtle text-text-secondary', icon: <PauseCircle aria-hidden /> },
  disconnected: { cls: 'border-warning/25 bg-warning-bg text-warning', icon: <Unplug aria-hidden /> },
  logged_out: { cls: 'border-warning/25 bg-warning-bg text-warning', icon: <LogOut aria-hidden /> },
  error: { cls: 'border-danger/25 bg-danger-bg text-danger', icon: <AlertOctagon aria-hidden /> },
};

export interface ConnectionStatusProps {
  state: ConnectionState;
  /** Short reason, e.g. "Sin red" or error code. */
  reason?: string | null;
  /** 0–100 while syncing, when supported. */
  progress?: number | null;
  size?: 'sm' | 'md';
  className?: string;
}

export function ConnectionStatus({ state, reason, progress, size = 'md', className }: ConnectionStatusProps) {
  const s = STATE_STYLE[state];
  return (
    <span className={cn('inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-0.5', className)}>
      <span
        data-state={state}
        className={cn(
          'inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill border font-medium [&_svg]:shrink-0',
          size === 'sm' ? 'px-2 py-0.5 text-helper [&_svg]:size-3.5' : 'px-2.5 py-1 text-body [&_svg]:size-4',
          s.cls,
        )}
      >
        {s.icon}
        {CONNECTION_STATE_LABELS[state]}
        {state === 'syncing' && typeof progress === 'number' ? <span className="cm-tabular">· {progress} %</span> : null}
      </span>
      {reason ? <span className="truncate text-helper text-text-secondary">{reason}</span> : null}
    </span>
  );
}

export interface QRPairingPanelProps {
  state: ConnectionState;
  /** Latest valid QR payload (only delivered over the private event stream). */
  qr: string | null;
  expiresAt?: Date | string | null;
  /** Injectable clock for tests/catalog. */
  now?: Date;
  connectionLabel?: string;
  onRetry?: () => void;
  onCancel?: () => void;
  errorMessage?: string | null;
  className?: string;
}

function useCountdown(expiresAt: Date | string | null | undefined, now?: Date): number | null {
  const [tick, setTick] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (now || !expiresAt) return;
    const t = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [expiresAt, now]);
  if (!expiresAt) return null;
  const ref = now ? now.getTime() : tick;
  return Math.max(0, Math.round((new Date(expiresAt).getTime() - ref) / 1000));
}

/** Pairing UI. Accepts mock state/QR — contains no session logic. */
export function QRPairingPanel({ state, qr, expiresAt, now, connectionLabel, onRetry, onCancel, errorMessage, className }: QRPairingPanelProps) {
  const remaining = useCountdown(expiresAt, now);
  const expired = remaining === 0;
  let visual: React.ReactNode;
  if (state === 'connected' || state === 'syncing') {
    visual = (
      <div className="flex size-full flex-col items-center justify-center gap-2 text-success">
        <CheckCircle2 aria-hidden className="size-12" />
        <p className="text-body font-medium">¡Número vinculado!</p>
      </div>
    );
  } else if (state === 'error' || state === 'logged_out') {
    visual = (
      <div className="flex size-full flex-col items-center justify-center gap-2 p-4 text-center text-danger">
        <PlugZap aria-hidden className="size-10" />
        <p className="text-body font-medium">{errorMessage ?? 'No se pudo vincular'}</p>
      </div>
    );
  } else if (qr && !expired && state === 'awaiting_qr') {
    visual = (
      <div className="rounded-menu bg-surface-default p-3">
        <QRCodeSVG value={qr} size={216} level="M" marginSize={0} role="img" aria-label="Código QR para vincular WhatsApp" />
      </div>
    );
  } else if (expired) {
    visual = (
      <div className="flex size-full flex-col items-center justify-center gap-3 p-4 text-center">
        <QrCode aria-hidden className="size-10 text-text-muted" />
        <p className="text-body text-text-secondary">El código expiró. Se generará uno nuevo automáticamente.</p>
      </div>
    );
  } else {
    visual = (
      <div className="flex size-full flex-col items-center justify-center gap-2 text-text-secondary" role="status">
        <Loader2 aria-hidden className="size-8 animate-spin" />
        <p className="text-body">Generando código QR…</p>
      </div>
    );
  }
  return (
    <div className={cn('@container', className)}>
    <div className="grid gap-6 @xl:grid-cols-[248px_minmax(0,1fr)] @xl:items-start">
      <div className="flex aspect-square w-full max-w-[248px] items-center justify-center justify-self-center rounded-card border border-border-subtle bg-surface-subtle">
        {visual}
      </div>
      <div className="grid gap-4">
        <div className="flex flex-wrap items-center gap-2">
          {connectionLabel ? <p className="text-card-title font-semibold text-text-primary">{connectionLabel}</p> : null}
          <ConnectionStatus state={state} size="sm" />
        </div>
        <ol className="grid gap-2 text-body text-text-primary">
          {[
            'Abre WhatsApp o WhatsApp Business en el teléfono del número de negocio.',
            'Ve a Configuración › Dispositivos vinculados › Vincular un dispositivo.',
            'Escanea este código con la cámara del teléfono.',
          ].map((step, i) => (
            <li key={step} className="flex gap-3">
              <span className="cm-tabular flex size-6 shrink-0 items-center justify-center rounded-pill bg-brand-tint text-helper font-semibold text-brand-hover">{i + 1}</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
        {remaining !== null && state === 'awaiting_qr' && !expired ? (
          <p className="cm-tabular text-helper text-text-secondary" aria-live="polite">
            El código se renueva en {remaining} s.
          </p>
        ) : null}
        <p className="flex gap-2 rounded-menu bg-surface-subtle p-3 text-helper text-text-secondary">
          <Smartphone aria-hidden className="mt-0.5 size-4 shrink-0" />
          Convometry solo lee conversaciones: nunca envía mensajes ni marca chats como leídos. Usa una integración no oficial de WhatsApp Web; la
          conexión puede requerir reconectar o volver a vincular.
        </p>
        {onRetry || onCancel ? (
          <div className="flex flex-wrap gap-2">
            {onRetry && (state === 'error' || state === 'logged_out' || expired) ? (
              <Button onClick={onRetry} leadingIcon={<RefreshCw aria-hidden />}>
                Reintentar
              </Button>
            ) : null}
            {onCancel ? (
              <Button variant="secondary" onClick={onCancel}>
                {state === 'connected' || state === 'syncing' ? 'Cerrar' : 'Cancelar'}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
    </div>
  );
}
